import { Component, Inject, inject, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ProfileFacade } from '@fantaz/profile/data-access';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AuthServiceInjectionToken,
  DevToolDetectionService,
  FzGoogleTagManagerService,
  MatchMakingService,
} from '@fantaz/shared/data-access/api';
import {
  BeginMatchResponseSuccess,
  CasualMatchParameters,
  Profile,
} from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { UntilDestroy } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { cloneDeep } from 'lodash';
type WebSocketMessage = {
  subtype: string;
  type: string;
  error?: string;
  clientMessage?: string;
  errorMessage?: string;
  noRetry?: boolean;
  success: boolean;
  matchResponse?: BeginMatchResponseSuccess;
  complete: boolean;
};
@UntilDestroy()
@Component({
  selector: 'fz-match-making-dialog',
  templateUrl: './match-making-dialog.component.html',
  styleUrls: ['./match-making-dialog.component.scss'],
})
export class MatchMakingDialogComponent implements OnInit {
  authService = inject(AuthServiceInjectionToken);
  profileFacade = inject(ProfileFacade);
  devToolDetectionService = inject(DevToolDetectionService);
  toastService = inject(ToastService);
  gtmService = inject(FzGoogleTagManagerService);
  dialogRef = inject(MatDialogRef<MatchMakingDialogComponent>);
  matchMakingService = inject(MatchMakingService);

  agree = false;
  count = 0;
  errorMessage: string | undefined = '';
  message: string | undefined;
  intentionalClose = false;
  processPercentComplete = 0;
  beginMatch: CasualMatchParameters | undefined;
  modalError = false;
  joiningGameEvent = false;
  hideRetry = false;
  playInstanceId: string;
  openZendeskAction = false;
  steps: {
    [key: string]: {
      complete: boolean;
      success: boolean;
      clientMessage: string;
    };
  };
  triggerErrorTimeout = 1000 * 15;

  objectKeys = Object.keys;
  messages: Record<string, WebSocketMessage> = {};
  time: NodeJS.Timeout;
  eventCode: string | undefined;
  userAutoPlay = false;
  profile = toSignal(this.profileFacade.profile$);
  requiredEventCode: boolean;
  constructor(@Inject(MAT_DIALOG_DATA) data: CasualMatchParameters) {
    this.beginMatch = data;
    this.eventCode = data.eventCode;
    this.beginMatch.requiredEventCode = true;

    this.requiredEventCode = true;

    if (data.openTournament.lifeExperience) {
      this.steps = {
        'Find Location': {
          complete: false,
          success: false,
          clientMessage: 'Capturing current location',
        },
        all: {
          complete: false,
          success: false,
          clientMessage: 'Reviewing tournament rules',
        },
        'validate-balance': {
          complete: false,
          success: false,
          clientMessage: this.beginMatch?.pass
            ? 'Validating pass'
            : 'Validating wallet balance',
        },
        'entry-fee-processed': {
          complete: false,
          success: false,
          clientMessage: this.beginMatch?.pass
            ? 'Processing pass'
            : 'Processing entry fees',
        },
      };
    } else {
      this.steps = {
        'Find Location': {
          complete: false,
          success: false,
          clientMessage: 'Capturing current location',
        },
        all: {
          complete: false,
          success: false,
          clientMessage: 'Reviewing tournament rules',
        },
        'validate-balance': {
          complete: false,
          success: false,
          clientMessage: 'Validating wallet balance',
        },
        'match-found': {
          complete: false,
          success: false,
          clientMessage: 'Finding match',
        },
        'entry-fee-processed': {
          complete: false,
          success: false,
          clientMessage: 'Processing entry fees',
        },
      };
    }
    this.messageCallback = this.messageCallback.bind(this);
    this.findMatchErrorCallback = this.findMatchErrorCallback.bind(this);
  }
  ngOnInit(): void {
    this.userAutoPlay = this.profile()?.userSettings?.autoPlay || false;
    if (this.beginMatch) {
      this.beginMatch.requiredEventCode =
        !this.userAutoPlay && !!this.eventCode;
    }
  }

  reset() {
    this.joiningGameEvent = true;
    this.errorMessage = '';
    this.modalError = false;
    this.hideRetry = false;
    this.messages = {};
  }

  findMatchErrorCallback() {
    this.clearErrorAfterDelay();
    this.errorMessage = 'Problem occurred while trying to join game.';
    this.joiningGameEvent = false;
    this.modalError = true;
    this.trowError({
      type: 'unknown',
      subtype: 'unknown',
      clientMessage: this.errorMessage,
      complete: false,
      success: false,
    });
  }

  messageCallback(message: WebSocketMessage, processPercentComplete = 0) {
    this.triggerErrorAfterDelay();
    this.processPercentComplete = processPercentComplete || 0;
    if (message.type === 'close') {
      this.errorMessage = message.clientMessage;
      this.modalError = true;
      this.trowError(message);
      this.joiningGameEvent = false;
      if (message.subtype) {
        this.hideRetry = true;
      }
      if (
        message.subtype === 'verification-failed' &&
        message.clientMessage === 'Monitoring systems blocked is not allowed.'
      ) {
        this.toastService.openSnackBar(
          'Please disable ad blockers or whitelist our site. Our monitoring is essential for fair play and game integrity, not for ads or data selling. Need help?',
        );
      }
      if (
        message.subtype === 'verification-failed' &&
        message.clientMessage === 'Monitoring systems blocked is not allowed.'
      ) {
        this.toastService.openSnackBar(
          'Please disable ad blockers or whitelist our site. Our monitoring is essential for fair play and game integrity, not for ads or data selling. Need help?',
        );
      }
    } else if (message.type === 'entry-fee-processed') {
      if (message.success === true && message.matchResponse) {
        this.finalize(message.matchResponse);
      }
    } else {
      if (!this.messages) {
        this.messages = {};
      }
      const type =
        message.subtype && message.subtype !== ''
          ? message.subtype
          : message.type;
      this.messages[type] = message;

      this.steps[type].complete = message.complete;
      this.steps[type].success = message.success;

      if (message.complete === true && message.success === false) {
        //completed with error
        this.modalError = true;
        this.trowError(message);
        this.joiningGameEvent = false;
        this.errorMessage = message.errorMessage;
        this.hideRetry = message.noRetry || false;
      }
    }
  }
  // Notify error for Dynatrace integration
  trowError(message: WebSocketMessage) {
    this.clearErrorAfterDelay();
    try {
      throw new Error(message.clientMessage);
    } catch (error) {
      console.error(error, message);
    }
  }

  start() {
    this.agree = true;
    this.authService
      .refreshSession()
      .pipe(take(1))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn.tokens) {
          this.initMatchMaking(this.beginMatch);
        }
      });
    this.triggerErrorAfterDelay();
  }

  /**
   * Sets a timeout to trigger an error callback and display a toast notification
   * after a delay of 15 seconds. If a specific error message is present, it will be displayed.
   * Otherwise, a default error message will be shown.
   */
  private triggerErrorAfterDelay() {
    this.clearErrorAfterDelay();
    this.time = setTimeout(() => {
      // Trigger the error callback
      this.trowError({
        type: 'unknown',
        subtype: 'unknown',
        clientMessage: 'Problem occurred while trying to join game.',
        complete: false,
        success: false,
      });

      // Display a toast notification with the error message
      // If no specific error message is present, a default message is used
      this.toastService.openSnackBar(
        'An error occurred while trying to join the game. Please try again.',
        'Close',
      );
    }, this.triggerErrorTimeout);
  }

  private clearErrorAfterDelay() {
    if (this.time) {
      clearTimeout(this.time);
    }
  }

  initMatchMaking(beginMatch: CasualMatchParameters | undefined) {
    if (beginMatch) {
      this.reset();
      if (this.userAutoPlay === true) {
        delete beginMatch.requiredEventCode;
        delete beginMatch.eventCode;
      }
      this.matchMakingService.initMatchMaking(
        beginMatch,
        this.messageCallback,
        this.findMatchErrorCallback,
      );
    }
  }
  finalize(successResponse: BeginMatchResponseSuccess) {
    this.dialogRef.close(successResponse);
  }
  retry() {
    this.start();
  }
  close(message?: string) {
    this.devToolDetectionService.stopDevtoolDetector();
    this.gtmService.pushTag({
      event: 'stop-prepare-game',
      userId: this.authService.getUserId(),
    });
    this.dialogRef.close(message);
  }

  redirectToRoom() {
    this.close('redirectToRoom');
  }

  refreshPassInfo() {
    this.close('refreshPassInfo');
  }

  redirect(page: string) {
    const url = new URL(page, window.location.origin);
    url.searchParams.append('redirectTo', window.location.href);
    window.location.href = url.toString();
  }

  continuePlayWithoutPass() {
    if (this.beginMatch && this.beginMatch.pass) {
      this.beginMatch.pass = undefined;
    }
    this.retry();
  }
  continuePlayWithAutoPlay() {
    if (this.beginMatch) {
      this.userAutoPlay = true;
      delete this.beginMatch.requiredEventCode;
      delete this.beginMatch.eventCode;
    }
    this.retry();
  }

  handleAutoPlay($event: MatCheckboxChange) {
    if ($event.checked) {
      this.userAutoPlay = true;
      const profile = this.profile();
      if (profile) {
        const settings = profile.userSettings
          ? cloneDeep(profile.userSettings)
          : {};

        settings.autoPlay = true;
        const payload: Profile = {
          ...profile,
          userSettings: settings,
        };
        this.profileFacade.updateProfileSettings(payload, {
          silent: true,
        });
      }
    }
  }
}
